// components/Pages/Login2.js - Complete Login Component with API Integration
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  loginUser,
  verifyMfaLogin,
  validateLoginResponse,
  storeUserData,
  clearUserData,
  startOrgEntryMfaChallenge,
} from "../../services/apiService";
import MfaVerification from "./MfaVerification";
import MfaMethodChoice from "./MfaMethodChoice";
import MfaLoginAuthenticatorSetup from "./MfaLoginAuthenticatorSetup";
import { removeAuthItem, setAuthItem, getAuthItem } from "../../services/authSessionStore";
import { logSecurityEvent } from "../../services/securityService";
import "../Pages/Login.scss";
import {
  parsePermissions,
  hasPermission,
  hasAnyModulePermission,
  getAllPermissionKeys,
  isPlatformOrSystemAdminRole,
  stampPlatformSuperAdminSession,
  isSessionSuperAdmin,
} from "../../utils/permissionUtils";
import { resolvePostLoginRoute, storeCredentialGateFromUser } from "../../utils/credentialGateUtils";
import { applySponsorCroLoginSession } from "../../utils/sponsorCroAccess";
import { clearOrgModulesHydrationAttempt } from "../../services/orgModulesHydrationService";
import { clearOrgPlatformFlagsHydrationAttempt } from "../../services/orgPlatformFlagsHydrationService";

const MFA_IN_PROGRESS_KEY = "aq_orgMfaInProgress";

function isOrgMfaRequired(value) {
  if (value === false || value === 0 || value === "0") return false;
  if (typeof value === "string" && value.trim().toLowerCase() === "false") {
    return false;
  }
  return true;
}

function pickSessionKey(session) {
  return (
    session?.APIPWD ||
    session?.apipwd ||
    session?.Sessionkey ||
    session?.sessionkey ||
    session?.SessionKey ||
    ""
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCodes, setAccessCodes] = useState(["", "", ""]);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [dynamicNumbers, setDynamicNumbers] = useState([]);
  const [sessionMessage, setSessionMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [loginStep, setLoginStep] = useState("credentials"); // credentials | mfaChoose | mfaAuthSetup | mfa
  const [pendingLoginResponse, setPendingLoginResponse] = useState(null);
  const [orgEntryMfaOnly, setOrgEntryMfaOnly] = useState(false);

  const navigate = useNavigate();

  // Check for logout reason and show message
  useEffect(() => {
    const logoutReason = localStorage.getItem("logoutReason");
    if (logoutReason === "session_expired") {
      setSessionMessage(
        "Your session has expired due to inactivity. Please sign in again.",
      );
      localStorage.removeItem("logoutReason");
      logSecurityEvent("session_expired_message_shown", {});
    }

    /* Do not wipe the session while Org-entry MFA is in progress (credentials
       already created a session; remount/HMR must not drop isLoggedIn). */
    let mfaInProgress = false;
    try {
      mfaInProgress = sessionStorage.getItem(MFA_IN_PROGRESS_KEY) === "1";
    } catch {
      mfaInProgress = false;
    }
    const hasExistingSession =
      !!getAuthItem("sessionToken") && !!getAuthItem("userData");
    if (!mfaInProgress && !hasExistingSession) {
      ["isLoggedIn", "lastActivity", "sessionTimeout"].forEach((key) =>
        removeAuthItem(key),
      );
    } else if (!mfaInProgress && hasExistingSession) {
      /* Visiting /login while a session cookie still exists (e.g. after a
         failed redirect). Keep userData/sessionToken; only reset activity. */
      setAuthItem("isLoggedIn", "true");
      setAuthItem("lastActivity", Date.now().toString());
    }

    logSecurityEvent("login_page_loaded", {});
  }, []);

  const resetAccessCodesAndIndexes = () => {
    const generateUniqueNumbers = () => {
      const numbers = new Set();
      while (numbers.size < 3) {
        // Generate random numbers between 1-6
        numbers.add(Math.floor(Math.random() * 6) + 1);
      }
      return Array.from(numbers);
    };

    const numbers = generateUniqueNumbers();
    setDynamicNumbers(numbers);
    setAccessCodes(["", "", ""]);

    logSecurityEvent("access_code_numbers_generated", {
      indexes: numbers,
    });
  };

  // Generate unique random numbers for placeholders
  useEffect(() => {
    resetAccessCodesAndIndexes();
  }, []);

  // Finalize login after credentials — MFA runs only when entering an MFA-on org
  const finalizeLogin = async (response) => {
    const validation = validateLoginResponse(response);
    if (!validation.isValid || validation.requiresMfa) {
      setError(validation.error || "Login failed.");
      logSecurityEvent("login_failed", {
        email: email,
        reason: validation.error,
      });
      resetAccessCodesAndIndexes();
      return false;
    }

    if (!validation.data.Role && validation.data.Roles) {
      validation.data.Role = validation.data.Roles;
    }

    // Drop any prior browser session so rights/flags from another user cannot leak.
    clearUserData();

    validation.data = stampPlatformSuperAdminSession(validation.data, null);

    validation.data.Permission = isPlatformOrSystemAdminRole(
      validation.data.Role || validation.data.Roles,
    )
      ? getAllPermissionKeys().join(",")
      : validation.data.Permission || "";

    validation.data = applySponsorCroLoginSession(validation.data);

    const loginSessionKey = pickSessionKey(validation.data);
    if (loginSessionKey) {
      validation.data.APIPWD = loginSessionKey;
      validation.data.Sessionkey = loginSessionKey;
    }

    clearOrgModulesHydrationAttempt();
    clearOrgPlatformFlagsHydrationAttempt();
    const stored = storeUserData(validation.data);
    if (!stored) {
      setError("Failed to store user data. Please try again.");
      return false;
    }

    try {
      const signatureBase64 = validation.data.SignatureBase64;
      if (signatureBase64) {
        sessionStorage.setItem("SignatureBase64", signatureBase64);
      } else {
        sessionStorage.removeItem("SignatureBase64");
      }
    } catch (e) {
      console.error("Error storing signature in session:", e);
    }

    storeCredentialGateFromUser(validation.data);

    const hasMultipleOrgs =
      response.data?.OtherOrgLink != null &&
      response.data.OtherOrgLink.length > 1;

    const mfaRequired =
      !hasMultipleOrgs &&
      !isSessionSuperAdmin(validation.data) &&
      isOrgMfaRequired(
        validation.data.MfaEnabled ??
          validation.data.mfaEnabled ??
          response.data?.MfaEnabled,
      );

    if (mfaRequired) {
      setPendingLoginResponse(response);
      try {
        sessionStorage.setItem(MFA_IN_PROGRESS_KEY, "1");
      } catch {
        /* ignore */
      }
      setAuthItem("isLoggedIn", "true");
      setAuthItem("lastActivity", Date.now().toString());
      try {
        const challengeResponse = await startOrgEntryMfaChallenge({
          staffId: validation.data.StaffId,
          emailId: validation.data.EmailId,
          organizationId: validation.data.OrganizationId,
          userName: validation.data.UserName,
        });
        if (challengeResponse?.success === 2 && challengeResponse.data) {
          setMfaChallenge(challengeResponse.data);
          setOrgEntryMfaOnly(true);
          setLoginStep("mfaChoose");
          logSecurityEvent("mfa_challenge_issued", { email, scope: "orgEntry" });
          return true;
        }
        try {
          sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
        } catch {
          /* ignore */
        }
      } catch (err) {
        try {
          sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
        } catch {
          /* ignore */
        }
        setError(err?.message || "Could not start MFA.");
        return false;
      }
    }

    try {
      sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
    } catch {
      /* ignore */
    }

    setSessionMessage("");
    logSecurityEvent("login_successful", {
      userId: validation.data.StaffId,
      email: validation.data.EmailId,
      role: validation.data.Role,
      organization: validation.data.OrganizationName,
    });

    navigate(resolvePostLoginRoute(validation.data, hasMultipleOrgs));
    return true;
  };

  const completeAfterOrgEntryMfa = async (verifiedToken) => {
    const loginResponse = pendingLoginResponse;
    const prior = loginResponse?.data;
    if (!prior) {
      setError("Login session expired. Please sign in again.");
      return false;
    }

    /* Do NOT call ChangeOrganization here. That endpoint is auth-protected and
       rotates LoginSession; a failed/partial rotate causes apiClient 401 →
       forceLogout → bounce straight back to /login after MFA. */
    void verifiedToken;

    const sessionKey = pickSessionKey(prior);
    if (!sessionKey) {
      setError("Session key missing after login. Please sign in again.");
      return false;
    }

    const sessionPayload = applySponsorCroLoginSession({
      ...prior,
      APIPWD: sessionKey,
      Sessionkey: sessionKey,
      OrganizationId: prior.OrganizationId || prior.Organizationid || "",
      OrganizationName:
        prior.OrganizationName || prior.OrgName || prior.organizationName || "",
      MfaEnabled: isOrgMfaRequired(prior.MfaEnabled ?? prior.mfaEnabled),
    });

    clearOrgModulesHydrationAttempt();
    clearOrgPlatformFlagsHydrationAttempt();
    const stored = storeUserData(sessionPayload);
    if (!stored) {
      setError("Failed to store user data. Please try again.");
      return false;
    }
    setAuthItem("isLoggedIn", "true");
    setAuthItem("lastActivity", Date.now().toString());
    setAuthItem("sessionTimeout", "15");
    storeCredentialGateFromUser(sessionPayload);

    try {
      sessionStorage.setItem(
        `aq_mfaVerifiedOrg_${String(sessionPayload.OrganizationId).toLowerCase()}`,
        "1",
      );
      sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
    } catch {
      /* ignore */
    }

    setPendingLoginResponse(null);
    setOrgEntryMfaOnly(false);
    setMfaChallenge(null);
    setSessionMessage("");

    const hasMultipleOrgs =
      Array.isArray(sessionPayload?.OtherOrgLink) &&
      sessionPayload.OtherOrgLink.length > 1;
    logSecurityEvent("login_successful", {
      userId: sessionPayload?.StaffId,
      email: sessionPayload?.EmailId,
      role: sessionPayload?.Role,
      organization: sessionPayload?.OrganizationName,
    });

    const dest = resolvePostLoginRoute(sessionPayload, hasMultipleOrgs);
    /* Full page load so ProtectedRoute/SessionContext read fresh cookies. */
    window.location.assign(dest);
    return true;
  };

  const handleMfaVerify = async (otpCode) => {
    setLoading(true);
    setError("");
    try {
      const token =
        mfaChallenge?.MfaSessionToken ?? mfaChallenge?.mfaSessionToken;
      const response = await verifyMfaLogin(token, otpCode);
      if (response.success !== 1) {
        setError(response.message || "Invalid verification code.");
        logSecurityEvent("mfa_verify_failed", {
          email,
          reason: response.message,
        });
        setLoading(false);
        return;
      }

      if (orgEntryMfaOnly) {
        const verifiedToken =
          response?.data?.MfaSessionToken ||
          response?.data?.mfaSessionToken ||
          token;
        await completeAfterOrgEntryMfa(verifiedToken);
        return;
      }

      try {
        sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
      } catch {
        /* ignore */
      }
      await finalizeLogin(response);
    } catch (err) {
      setError(err?.message || "Verification failed. Please try again.");
      logSecurityEvent("mfa_verify_error", { email, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaBack = () => {
    try {
      sessionStorage.removeItem(MFA_IN_PROGRESS_KEY);
    } catch {
      /* ignore */
    }
    setLoginStep("credentials");
    setMfaChallenge(null);
    setPendingLoginResponse(null);
    setOrgEntryMfaOnly(false);
    setError("");
    resetAccessCodesAndIndexes();
  };

  const handleMfaMethodSelected = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setLoginStep("mfa");
    setError("");
  };

  const handleMfaSetupRequired = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setLoginStep("mfaAuthSetup");
    setError("");
  };

  const handleMfaSetupComplete = (updatedChallenge) => {
    setMfaChallenge(updatedChallenge);
    setLoginStep("mfa");
    setError("");
  };

  const handleMfaSetupBack = () => {
    setLoginStep("mfaChoose");
    setError("");
  };

  // MAIN SUBMIT HANDLER - credentials step (MFA phase 1)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Basic validation
    if (!email || !password) {
      setError("Please enter email and password");
      setLoading(false);
      return;
    }

    // STEP 2: Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // STEP 3: Validate access codes
    if (accessCodes.some((code) => !code)) {
      setError("Please enter all access codes");
      setLoading(false);
      return;
    }

    // STEP 4: Validate access codes are numbers
    if (accessCodes.some((code) => !/^\d$/.test(code))) {
      setError("Access codes must be single digits (0-9)");
      setLoading(false);
      return;
    }

    try {
      // STEP 5: Prepare login data for API
      const loginData = {
        email: email.trim(),
        password: password,
        accessCodeIndexes: dynamicNumbers, // The random numbers [2, 5, 1]
        accessCodeValues: accessCodes, // User entered values ["3", "7", "9"]
      };

      // STEP 6: Call your API using the apiService
      const response = await loginUser(loginData);

      const validation = validateLoginResponse(response);

      /* MFA required — show verification step */
      if (validation.requiresMfa) {
        setMfaChallenge(validation.mfaChallenge);
        setLoginStep("mfaChoose");
        setLoading(false);
        logSecurityEvent("mfa_challenge_issued", { email });
        return;
      }

      if (!validation.isValid) {
        setError(validation.error);
        logSecurityEvent("login_failed", {
          email: email,
          reason: validation.error,
        });
        resetAccessCodesAndIndexes();
        setLoading(false);
        return;
      }

      await finalizeLogin(response);
    } catch (error) {
      console.error("Login error:", error);

      // Handle different types of errors
      let errorMessage = "Login failed. Please try again.";

      if (error.message === "Request timeout") {
        errorMessage =
          "Request timed out. Please check your connection and try again.";
      } else if (error.message.includes("HTTP error! status: 400")) {
        errorMessage =
          "Invalid login credentials. Please check your email, password, and access codes.";
      } else if (error.message.includes("HTTP error! status: 401")) {
        errorMessage = "Access denied. Please check your credentials.";
      } else if (error.message.includes("HTTP error! status: 500")) {
        errorMessage =
          "Server error. Please try again later or contact support.";
      } else if (error.message.includes("Failed to fetch")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.message.includes("NetworkError")) {
        errorMessage =
          "Cannot connect to server. Please check if the server is running.";
      }

      setError(errorMessage);
      resetAccessCodesAndIndexes();
      logSecurityEvent("login_error", {
        email: email,
        error: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle access code input changes
  const handleAccessCodeChange = (index, value) => {
    // Only allow single digits 0-9
    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newAccessCodes = [...accessCodes];
    newAccessCodes[index] = value;
    setAccessCodes(newAccessCodes);

    // Auto-focus next input if current input has value
    if (value && index < 2) {
      const nextInput = document.getElementById(`access-code-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, index) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace" && !accessCodes[index] && index > 0) {
      const prevInput = document.getElementById(`access-code-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  // Convert number to ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };

  return (
    <div className="login2-container">
      {/* Left Side - Branding Section */}
      <div className="login2-left">
        <div className="login2-bg-image"></div>
        <div className="login2-gradient-overlay-1"></div>
        <div className="login2-gradient-overlay-2"></div>
        <div className="login2-blur-circle-1"></div>
        <div className="login2-blur-circle-2"></div>

        <div className="login2-left-content">
          {/* Logo */}
          <div className="login2-logo">
            <div className="login2-logo-icon">
              <img
                src="data:image/png;base64,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"
                alt="Logo"
                className="login2-logo-img"
              />
            </div>
            <span className="login2-logo-text">AQ Trials</span>
          </div>

          {/* Main Heading */}
          <h2 className="login2-heading">
            Your Digital <br />
            <span className="login2-heading-accent">Trial Control Centre </span>
          </h2>

          {/* Description */}
          <p className="login2-description">
            Enter a workspace designed for clarity and control. Experience
            organised documentation, transparent compliance oversight, and
            seamless collaboration across your entire research network.
          </p>

          {/* Trusted By Section */}
          <div className="login2-trusted">
            <div className="login2-avatars">
              <div className="login2-avatar login2-avatar-blue" title="Royal Free">RF</div>
              <div className="login2-avatar login2-avatar-indigo" title="St George's Unviersity hospital"> SG</div>
              <div className="login2-avatar login2-avatar-cyan" title="NHS">NHS</div>
            </div>
            <span className="login2-trusted-text">
              Trusted by leading research teams
            </span>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login2-right">
        <div className="login2-form-wrapper">
          {/* Mobile Logo */}
          <div className="login2-mobile-logo">
            <div className="login2-mobile-logo-icon">
              <img
                src="data:image/png;base64,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"
                alt="Logo"
                className="login2-logo-img"
              />
            </div>
            <h2 className="login2-mobile-logo-text">AQ Trials</h2>
          </div>

          <div className="login2-form-header">
            <h1 className="login2-form-title">
              {loginStep === "mfa" ||
              loginStep === "mfaChoose" ||
              loginStep === "mfaAuthSetup"
                ? "Verify your identity"
                : "Log in to your account"}
            </h1>
          </div>

          {loginStep === "mfaChoose" && mfaChallenge ? (
            <MfaMethodChoice
              challenge={mfaChallenge}
              onSelected={handleMfaMethodSelected}
              onSetupRequired={handleMfaSetupRequired}
              onBack={handleMfaBack}
              loading={loading}
            />
          ) : loginStep === "mfaAuthSetup" && mfaChallenge ? (
            <MfaLoginAuthenticatorSetup
              challenge={mfaChallenge}
              onComplete={handleMfaSetupComplete}
              onBack={handleMfaSetupBack}
              loading={loading}
            />
          ) : loginStep === "mfa" && mfaChallenge ? (
            <MfaVerification
              challenge={mfaChallenge}
              onVerify={handleMfaVerify}
              onBack={handleMfaBack}
              onChallengeUpdate={setMfaChallenge}
              onSetupRequired={handleMfaSetupRequired}
              loading={loading}
              error={error}
            />
          ) : (
          <form className="login2-form" onSubmit={handleSubmit}>
            {/* Error message */}
            {error && (
              <div className="login2-alert login2-alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Session timeout message */}
            {sessionMessage && (
              <div className="login2-alert login2-alert-warning" role="alert">
                {sessionMessage}
              </div>
            )}

            <div className="login2-form-fields">
              {/* Email Field */}
              <div className="login2-form-group">
                <label className="login2-form-label" htmlFor="email">
                  Email Address
                </label>
                <div className="login2-input-wrapper">
                  <input
                    id="email"
                    type="email"
                    className="login2-form-input"
                    placeholder="name@clinicaltrial.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="login2-form-group">
                <label className="login2-form-label" htmlFor="password">
                  Password
                </label>
                <div className="login2-password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="login2-form-input login2-password-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login2-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i
                      className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Access Code Fields */}
            <div className="login2-form-group">
              <div className="login2-access-code-section">
                <label className="login2-form-label">Enter Access Code *</label>
                <div className="login2-access-code-inputs">
                  {accessCodes.map((code, index) => (
                    <div key={index} className="login2-access-code-box">
                      <input
                        id={`access-code-${index}`}
                        type="password"
                        className="login2-form-input login2-access-code-input"
                        placeholder={
                          dynamicNumbers[index]
                            ? getOrdinalSuffix(dynamicNumbers[index])
                            : ""
                        }
                        value={code}
                        onChange={(e) =>
                          handleAccessCodeChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        maxLength="1"
                        disabled={loading}
                        required
                        autoComplete="off"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="login2-form-options">
              <div className="login2-remember-me-wrapper">
                <input
                  id="remember-me"
                  type="checkbox"
                  className="login2-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <label
                  className="login2-remember-me-label"
                  htmlFor="remember-me"
                >
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" className="login2-forgot-password">
                Forgot your password?
              </Link>
            </div>

            {/* Submit Button */}
            <div className="login2-submit-wrapper">
              <button
                type="submit"
                className="login2-btn-signin"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="login2-spinner"></span>
                    Signing In...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </div>
          </form>
          )}

          {/* Footer Links */}
          <div className="login2-footer">
            <Link to="/legal-policies" className="login2-footer-link">
              Legal Policies
            </Link>
            <span className="login2-footer-separator">·</span>
            <a
              href="mailto:support@aq-trials.com"
              className="login2-footer-link"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
